/**
 * AddByTeamModal Component
 *
 * Multi-step modal for admin team enrollment via manager direct-reports lookup.
 * Step 1: Search for a manager using UserSearchSelect
 * Step 2: Fetch and display direct reports with checkboxes
 * Step 3: Bulk enroll selected users
 * Step 4: Display enrollment summary
 */

import { useState, useMemo } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import Spinner from '@cloudscape-design/components/spinner';
import { UserSearchSelect } from './UserSearchSelect';
import { UserSearchResult, DirectReport } from '../api/users';
import { useDirectReports } from '../hooks/queries/useUsers';
import { useBulkEnroll } from '../hooks/queries/useCohorts';
import { badgePhotoUrl } from '../lib/externalUrls';

export interface AddByTeamModalProps {
  visible: boolean;
  cohortId: string;
  programId: string;
  onDismiss: () => void;
  onSuccess: () => void;
}

type Step = 'search' | 'select' | 'enrolling' | 'summary';

interface EnrollmentSummary {
  successCount: number;
  failureCount: number;
  total: number;
}

export function AddByTeamModal({
  visible,
  cohortId,
  programId: _programId,
  onDismiss,
  onSuccess,
}: AddByTeamModalProps) {
  // programId is accepted for interface consistency with AddParticipantModal
  void _programId;
  const [step, setStep] = useState<Step>('search');
  const [selectedManager, setSelectedManager] = useState<UserSearchResult | null>(null);
  const [selectedReports, setSelectedReports] = useState<DirectReport[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<EnrollmentSummary | null>(null);

  const {
    data: directReports,
    isLoading: isLoadingReports,
    isError: isReportsError,
  } = useDirectReports(selectedManager?.login);

  const bulkEnroll = useBulkEnroll();

  const resetState = () => {
    setStep('search');
    setSelectedManager(null);
    setSelectedReports([]);
    setError(null);
    setSummary(null);
  };

  const handleDismiss = () => {
    if (step !== 'enrolling') {
      if (step === 'summary' && summary && summary.successCount > 0) {
        onSuccess();
      }
      resetState();
      onDismiss();
    }
  };

  const handleManagerSelect = (user: UserSearchResult) => {
    setSelectedManager(user);
    setSelectedReports([]);
    setError(null);
    setStep('select');
  };

  const handleBack = () => {
    setSelectedManager(null);
    setSelectedReports([]);
    setError(null);
    setStep('search');
  };

  const handleConfirm = () => {
    if (selectedReports.length === 0) return;

    setStep('enrolling');
    setError(null);

    const users = selectedReports.map((r) => ({
      userId: r.login,
      userName: r.name,
      userEmail: r.email || `${r.login}@amazon.com`,
    }));

    bulkEnroll.mutate(
      { cohortId, users },
      {
        onSuccess: () => {
          setSummary({
            successCount: users.length,
            failureCount: 0,
            total: users.length,
          });
          setStep('summary');
        },
        onError: (err: any) => {
          const message = err?.message || 'Failed to enroll users';
          setSummary({
            successCount: 0,
            failureCount: users.length,
            total: users.length,
          });
          setError(message);
          setStep('summary');
        },
      },
    );
  };

  const handleDone = () => {
    if (summary && summary.successCount > 0) {
      onSuccess();
    }
    resetState();
    onDismiss();
  };

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: (item: DirectReport) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img
              src={badgePhotoUrl(item.login)}
              alt={item.name}
              style={{ width: 32, height: 32, borderRadius: 5, objectFit: 'cover' as const, flexShrink: 0 }}
            />
            <span>{item.name}</span>
          </div>
        ),
        sortingField: 'name',
      },
      {
        id: 'alias',
        header: 'Alias',
        cell: (item: DirectReport) => item.login,
        sortingField: 'login',
      },
      {
        id: 'title',
        header: 'Title',
        cell: (item: DirectReport) => item.title,
        sortingField: 'title',
      },
    ],
    [],
  );

  const renderSearchStep = () => (
    <SpaceBetween size="m">
      <Box variant="p">Search for a manager to view their direct reports.</Box>
      <UserSearchSelect
        onSelect={handleManagerSelect}
        placeholder="Search for a manager by name or alias..."
        managersOnly
      />
    </SpaceBetween>
  );

  const renderSelectStep = () => {
    if (isLoadingReports) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
          <Box variant="p" margin={{ top: 's' }}>
            Loading direct reports for {selectedManager?.name}...
          </Box>
        </Box>
      );
    }

    if (isReportsError) {
      return (
        <Alert type="error">
          Unable to look up direct reports. The user lookup service is temporarily unavailable.
        </Alert>
      );
    }

    if (!directReports || directReports.length === 0) {
      return (
        <Box textAlign="center" color="text-status-info" padding="l">
          <Box variant="p">No direct reports found for this manager.</Box>
        </Box>
      );
    }

    return (
      <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
        <Table
          columnDefinitions={columnDefinitions}
          items={directReports}
          selectionType="multi"
          selectedItems={selectedReports}
          onSelectionChange={({ detail }) =>
            setSelectedReports(detail.selectedItems)
          }
          trackBy="login"
          header={
            <Header
              counter={`(${directReports.length})`}
              description={`Direct reports for ${selectedManager?.name} (${selectedManager?.login})`}
            >
              Select team members
            </Header>
          }
          empty={
            <Box textAlign="center" color="text-status-info" padding="l">
              <Box variant="p">No direct reports found for this manager.</Box>
            </Box>
          }
        />
      </div>
    );
  };

  const renderEnrollingStep = () => (
    <Box textAlign="center" padding="l">
      <Spinner size="large" />
      <Box variant="p" margin={{ top: 's' }}>
        Enrolling {selectedReports.length} user{selectedReports.length !== 1 ? 's' : ''}...
      </Box>
    </Box>
  );

  const renderSummaryStep = () => (
    <SpaceBetween size="m">
      {error && (
        <Alert type="error">{error}</Alert>
      )}
      {summary && summary.successCount > 0 && (
        <Alert type="success">
          Successfully enrolled {summary.successCount} of {summary.total} user{summary.total !== 1 ? 's' : ''}.
        </Alert>
      )}
      {summary && summary.failureCount > 0 && summary.successCount === 0 && (
        <Alert type="error">
          Failed to enroll {summary.failureCount} of {summary.total} user{summary.total !== 1 ? 's' : ''}.
        </Alert>
      )}
    </SpaceBetween>
  );

  const renderStepContent = () => {
    switch (step) {
      case 'search':
        return renderSearchStep();
      case 'select':
        return renderSelectStep();
      case 'enrolling':
        return renderEnrollingStep();
      case 'summary':
        return renderSummaryStep();
    }
  };

  const renderFooter = () => {
    if (step === 'summary') {
      return (
        <Box float="right">
          <Button variant="primary" onClick={handleDone}>
            Done
          </Button>
        </Box>
      );
    }

    if (step === 'enrolling') {
      return null;
    }

    return (
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={handleDismiss}>
            Cancel
          </Button>
          {step === 'select' && (
            <>
              <Button onClick={handleBack}>Back</Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={selectedReports.length === 0}
              >
                Add {selectedReports.length > 0 ? `(${selectedReports.length})` : ''}
              </Button>
            </>
          )}
        </SpaceBetween>
      </Box>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Add by team"
      size="large"
      footer={renderFooter()}
    >
      <SpaceBetween size="m">
        {renderStepContent()}
      </SpaceBetween>
    </Modal>
  );
}
